import React, { ChangeEvent, useCallback } from 'react';
import { setTimelineScale } from '@/redux/slices/packagingSlice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectPackagingTimelineScale } from '@/redux/selectors/packagingSelectors';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { SEGMENT_CONTAINER_PADDING, TIMELINE_MAX_SCALE, TIMELINE_MIN_SCALE } from '@/components/Packaging/constants';
import PolarisIcon from '@/components/_common/PolarisIcon';

export const ZoomControl: React.FC = () => {
  const dispatch = useAppDispatch();
  const timelineScale = useAppSelector(selectPackagingTimelineScale);

  const handleZoomChange = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(setTimelineScale(e.target.valueAsNumber / 100));
  };

  const handleZoomOutButtonClick = useCallback(() => {
    // Calculate what the minimum scale should be to keep content readable
    const minReadableScale = Math.max(TIMELINE_MIN_SCALE, 0.2);
    const newScale = Math.max(minReadableScale, timelineScale - timelineScale / 5);
    dispatch(setTimelineScale(newScale));
  }, [timelineScale]);

  const handleZoomInButtonClick = useCallback(() => {
    const newScale = Math.min(TIMELINE_MAX_SCALE, timelineScale + timelineScale / 5);
    dispatch(setTimelineScale(newScale));
  }, [timelineScale]);

  return (
    <Wrapper data-prevent-timeline-seek>
      <InnerWrapper>
        <IconButton variant="text" size="small" onClick={handleZoomOutButtonClick}>
          <PolarisIcon icon="subtract" width={17} height={17} />
        </IconButton>
        <Input
          value={timelineScale * 100}
          type="range"
          min={TIMELINE_MIN_SCALE * 100}
          max={TIMELINE_MAX_SCALE * 100}
          step="0.1"
          onChange={handleZoomChange}
        />
        <IconButton variant="text" size="small" onClick={handleZoomInButtonClick}>
          <PolarisIcon icon="add" width={17} height={17} />
        </IconButton>
      </InnerWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: sticky;
  left: 0;
  display: inline-block;
  margin-left: ${SEGMENT_CONTAINER_PADDING}px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: rgba(0, 0, 0, 0.2);
  font-size: 0.8rem;
`;

const InnerWrapper = styled.div`
  display: flex;
  align-items: stretch;
  height: 25px;
`;

const Input = styled.input`
  width: 103px;
  -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
  background: transparent; /* Otherwise white in Chrome */

  ${() => {
    const thumbColor = '#4357f2';
    const thumbStyles = `
        -webkit-appearance: none;
        width: 15px;
        height: 15px;
        margin-top: -7.5px;
        border-radius: 50%;
        background: ${thumbColor};
      `;

    const trackColor = '#A3A8BA';
    const trackStyles = `
      height: 1px;
      background: ${trackColor};
    `;

    /**
     *  Webkit and Firefox styles can not be combined in one selector divided by coma. In that case browsers will
     *  ignore the whole rule.
     */
    return `
      /* Webkit styles */
      &::-webkit-slider-thumb {
        ${thumbStyles}
      }
      &::-webkit-slider-thumb:hover { 
        background: hsl(from ${thumbColor} h s calc(l - 5)); 
      }
      
      &::-webkit-slider-runnable-track {
        ${trackStyles}
      }
      &:hover::-webkit-slider-runnable-track { 
        background: hsl(from ${trackColor} h s calc(l - 10)); 
      }
    
      /* Firefox styles */
      &::-moz-range-thumb {
        ${thumbStyles}
      }
      &::-moz-range-thumb:hover { 
        background: hsl(from ${thumbColor} h s calc(l - 5)); 
      }
      
      &::-moz-range-track {
        ${trackStyles}
      }
      &:hover::-moz-range-track { 
        background: hsl(from ${trackColor} h s calc(l - 5)); 
      }
    `;
  }}
`;

const IconButton = styled(Button)`
  min-width: 0;
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#a3a8ba' : '#8d90a1')};
`;
